import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Countdown from "@/components/Countdown";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Download, FileText, Send, Clock, PlusCircle, Receipt, FileDown, CalendarClock } from "lucide-react";
import { toast } from "sonner";

export const STATUS_LABELS = { entwurf: "Entwurf", eingegangen: "Eingegangen", in_pruefung: "In Prüfung", erstellt: "Erstellt", fertig: "Fertig" };
const FLOW = ["eingegangen", "in_pruefung", "erstellt", "fertig"];

export function StatusTracker({ status }) {
  const idx = FLOW.indexOf(status);
  return (
    <div className="flex items-center justify-between mt-2">
      {FLOW.map((s, i) => (
        <div key={s} className="flex items-center flex-1 last:flex-none">
          <div className="flex flex-col items-center">
            <div className={`h-8 w-8 rounded-full grid place-items-center text-xs font-semibold ${i <= idx ? "bg-emerald-500 text-white" : "bg-muted text-muted-foreground border border-border"}`}>{i + 1}</div>
            <span className="text-[10px] mt-1 text-center w-16">{STATUS_LABELS[s]}</span>
          </div>
          {i < FLOW.length - 1 && <div className={`h-0.5 flex-1 mx-1 -mt-4 ${i < idx ? "bg-emerald-500" : "bg-border"}`} />}
        </div>
      ))}
    </div>
  );
}

export async function exportPdf(appId) {
  try {
    const res = await api.get(`/applications/${appId}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "Fachkundige-Stellungnahme.pdf"; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error("PDF-Export fehlgeschlagen"); }
}

export async function downloadFile(appId, file) {
  try {
    const res = await api.get(`/files/${appId}/${file.id}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = file.filename; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error("Download fehlgeschlagen"); }
}

export default function Portal() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const endRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/applications");
    setApps(data);
    const cur = data.find((a) => a.paid) || data[0];
    if (cur) { setActive(cur); loadMessages(cur.id); }
  };
  const loadMessages = async (id) => { const { data } = await api.get(`/applications/${id}/messages`); setMessages(data); };
  useEffect(() => { load(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!msg.trim()) return;
    try { await api.post(`/applications/${active.id}/messages`, { text: msg }); setMsg(""); loadMessages(active.id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Mein Portal</h1>
            <p className="text-muted-foreground text-sm">Willkommen, {user?.name}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="book-appointment" onClick={() => nav("/termin")} className="border-[#0A192F] text-[#0A192F]"><CalendarClock size={16} className="mr-1" /> Beratungstermin</Button>
            <Button data-testid="new-request" onClick={() => nav("/antrag")} className="bg-emerald-500 hover:bg-emerald-600 text-white"><PlusCircle size={16} className="mr-1" /> Neue Anfrage</Button>
          </div>
        </div>

        {apps.length === 0 && (
          <Card className="p-10 text-center border-dashed">
            <p className="text-muted-foreground">Sie haben noch keine Anfrage gestellt.</p>
            <Button className="mt-4 bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => nav("/antrag")}>Jetzt starten</Button>
          </Card>
        )}

        {active && (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Status hero */}
              <Card className="p-6 border-border shadow-sm bg-[#0A192F] text-white">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"><Clock size={12} className="mr-1" /> {STATUS_LABELS[active.status]}</Badge>
                    <h2 className="text-lg font-semibold mt-2">Ihre fachkundige Stellungnahme</h2>
                  </div>
                  {active.paid && active.deadline_at && active.status !== "fertig" && (
                    <div className="text-right">
                      <div className="text-xs text-slate-400 uppercase tracking-widest mb-1">Verbleibende Zeit</div>
                      <Countdown deadline={active.deadline_at} />
                    </div>
                  )}
                </div>
                {active.paid ? <div className="mt-6"><StatusTracker status={active.status} /></div> : active.paypal_pending ? (
                  <div className="mt-4">
                    <p className="text-amber-300 text-sm" data-testid="paypal-pending">Zahlung via PayPal ausstehend. Sobald wir den Zahlungseingang bestätigt haben, startet Ihr 48-Stunden-Countdown.</p>
                  </div>
                ) : (
                  <div className="mt-4">
                    <p className="text-slate-300 text-sm">Diese Anfrage ist noch nicht bezahlt.</p>
                    <Button className="mt-3 bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => nav("/antrag")}>Anfrage abschließen</Button>
                  </div>
                )}
              </Card>

              {/* Downloads */}
              <Card className="p-6 border-border shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">Dokumente</h3>
                  {active.paid && (
                    <Button size="sm" variant="outline" data-testid="export-pdf"
                      onClick={() => exportPdf(active.id)} className="border-[#0A192F] text-[#0A192F]">
                      <FileDown size={14} className="mr-1" /> Als PDF exportieren
                    </Button>
                  )}
                </div>
                {active.final_file && (
                  <div className="flex items-center justify-between border border-emerald-200 bg-emerald-50 rounded-md p-3 mb-3">
                    <span className="flex items-center gap-2 font-medium text-emerald-800"><FileText size={16} /> {active.final_file.filename} (Fertige Stellungnahme)</span>
                    <Button size="sm" data-testid="download-final" onClick={() => downloadFile(active.id, active.final_file)} className="bg-emerald-500 hover:bg-emerald-600 text-white"><Download size={14} className="mr-1" /> Download</Button>
                  </div>
                )}
                {active.invoice && (
                  <div className="flex items-center justify-between border border-border rounded-md p-3 mb-3">
                    <span className="flex flex-col text-sm">
                      <span className="flex items-center gap-2"><Receipt size={16} /> Rechnung {active.invoice.number} · {active.invoice.amount} €</span>
                      {active.invoice.vat_amount != null && (
                        <span className="text-xs text-muted-foreground ml-6">Netto {active.invoice.net} € · zzgl. {active.invoice.vat_rate}% USt {active.invoice.vat_amount} €</span>
                      )}
                    </span>
                    <Badge variant="secondary">Bezahlt</Badge>
                  </div>
                )}
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-4 mb-2">Ihre hochgeladenen Unterlagen</p>
                {(active.files || []).length === 0 && <p className="text-sm text-muted-foreground">Keine Dokumente.</p>}
                {(active.files || []).map((f) => (
                  <div key={f.id} className="flex items-center justify-between text-sm py-1.5">
                    <span className="flex items-center gap-2"><FileText size={14} className="text-muted-foreground" /> {f.filename}</span>
                    <button onClick={() => downloadFile(active.id, f)} className="text-emerald-600 hover:underline">Öffnen</button>
                  </div>
                ))}
              </Card>
            </div>

            {/* Messages + list */}
            <div className="space-y-6">
              <Card className="p-4 border-border shadow-sm flex flex-col h-[400px]">
                <h3 className="font-semibold mb-2">Nachrichten mit dem Gutachter</h3>
                <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                  {messages.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Nachrichten.</p>}
                  {messages.map((m) => (
                    <div key={m.id} className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${m.sender_id === user.id ? "ml-auto bg-emerald-500 text-white" : "bg-muted"}`}>
                      <div className="text-[10px] opacity-70 mb-0.5">{m.sender_name}</div>{m.text}
                    </div>
                  ))}
                  <div ref={endRef} />
                </div>
                <div className="flex gap-2 mt-2">
                  <Input data-testid="msg-input" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Nachricht…" />
                  <Button data-testid="msg-send" onClick={send} size="icon" className="bg-[#0A192F] hover:bg-[#172A45] text-white"><Send size={16} /></Button>
                </div>
              </Card>

              <Card className="p-4 border-border shadow-sm">
                <h3 className="font-semibold mb-2">Alle Anfragen</h3>
                {apps.map((a) => (
                  <button key={a.id} data-testid={`app-item-${a.id}`} onClick={() => { setActive(a); loadMessages(a.id); }}
                    className={`w-full text-left rounded-md px-3 py-2 text-sm flex justify-between items-center ${active.id === a.id ? "bg-muted" : "hover:bg-muted/60"}`}>
                    <span>{a.lookup_key ? "Stellungnahme" : "Entwurf"}</span>
                    <Badge variant="secondary">{STATUS_LABELS[a.status]}</Badge>
                  </button>
                ))}
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
